import {Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, ViewEncapsulation} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Mime} from '../../Model/Catalog/Mime';
import {Product} from '../../Model/Catalog/Product';
import {NextPimField} from '../../Model/Catalog/NextPimField';
import {CatalogService} from '../../Services/CatalogManagement/catalog.service';
import {LoginService} from '../../Services/login.service';
import {TemplateService} from '../../Services/template.service';
import {ToolTipService} from '../../Services/tooltip.service';
import {UserManagementService} from '../../Services/userManagment.service';
import {StringResponse} from '../../Model/Dto/StringResponse';
import {DragInfo} from '../../Model/ui/DragInfo';
import {ValidationService} from '../../Services/Validation/validation.service';
import {SystemService} from '../../Services/system.service';
import {confirm} from 'devextreme/ui/dialog';
import {ExcelElement} from '../../Model/ui/ExcelElement';
import {NpInputControlContext} from "./np-input-control-context";
import {FallbackEventEmitter} from "./fallback-event-emitter";
import DevExpress from "devextreme";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import ItemDeletingEvent = DevExpress.ui.dxList.ItemDeletingEvent;


@Component({
    selector: 'np-inputControl',
    templateUrl: 'inputControl.component.html',
    styleUrls: ['inputControl.component.css'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class NpInputControlComponent implements OnInit {
    private context = inject(NpInputControlContext, {
        optional: true
    });

    @Input() placeLabelPosition: 'left' | 'right' | null = null;
    @Input() showTranslationPlaceLabel: boolean = false;
    @Input() suppressLabel: boolean = false;
    @Input() suppressLinkButton: boolean = false;
    @Input() suppressTranslation: boolean = true;
    @Input() suppressPlaceLabel: boolean = false;
    @Input() suppressDragHandle: boolean = false;
    @Input() suppressContentWidth: boolean = false;
    @Input() suppressInfoText: boolean = false;
    @Input() infoText: string = '';
    @Input() labelText: string = '';
    @Input() field: string = '';
    @Input() direction: 'row' | 'column' = 'row';
    @Input() showHTMLSwitch: boolean = false;
    @Input() showHTMLEditor: boolean = false;
    @Input() suppressToolTip: boolean = false;
    @Input() showPlaceLabel: boolean = false;

    @Output() showHTMLEditorChange = new EventEmitter<any>();

    @Input() model: Product = this.context?.model ?? new Product();
    @Input() productTranslation: Product = this.context?.productTranslation ?? null;
    @Input() showLinkButtons: boolean = this.context?.showLinkButtons ?? false;
    @Input() selectedElements: string[] = this.context?.selectedElements ?? [''];
    @Input() dragFilter: string = this.context?.dragFilter ?? '';
    @Input() isPrint: boolean = this.context?.isPrint ?? false;
    @Input() showDragHandle: boolean = this.context?.showDragHandle;

    @Output() elementSelected = new FallbackEventEmitter<string>(this.context?.elementSelected);
    @Output() onValueChanged = new FallbackEventEmitter<any>(this.context?.valueChanged);
    @Output() excelElementDropped = new FallbackEventEmitter<any>(this.context?.elementDropped);

    _system: string;
    get system(): string {
        return this._system;
    }

    @Input('system') set system(value: string | number) {
        if (value != undefined) {
            this._system = value.toString();
            this.dragInfo.dSystem = this._system;
        }
    }

    _element: string;
    get element(): string {
        return this._element;
    }

    @Input('element') set element(value: string | number) {
        if (value != undefined) {
            this._element = value.toString();
            this.dragInfo.dElement = this._element;
        }
    }

    showToolTips: boolean = false;
    showProToolTips: boolean = false;
    showPlaceHolders: boolean = true;
    showInfoText: boolean = true;
    showRemoveFromTemplateItem: boolean = false;
    dragInfo: DragInfo = new DragInfo();

    public toolTipVisible: boolean = false;
    public toolTipTargetId: string = '';

    linkedItems: ExcelElement[] = [];
    fieldValueClass: string = '';
    private destroyRef = inject(DestroyRef);

    constructor(
        public translate: TranslateService,
        public validationService: ValidationService,
        public templateService: TemplateService,
        public toolTipServiceService: ToolTipService,
        public loginService: LoginService,
        public userManagement: UserManagementService,
        public catalogService: CatalogService,
        public systemService: SystemService
    ) {
    }

    ngOnInit(): void {
        this.showToolTips = this.loginService.showToolTips;
        this.showProToolTips = this.loginService.showProToolTips;
        this.showPlaceHolders = this.loginService.showPlaceHolders;
        this.showInfoText = this.loginService.showInfoText;

        if (!this.suppressToolTip) {
            this.toolTipTargetId = `${this.field}_${this.system}_${this.element}`;
        }
        this.dragInfo.dModel = this.model;
        this.dragInfo.dField = this.field;
        this.dragInfo.dDisplayName = '';
        if (this.element == undefined) {
            this.element = '-1';
        }
        if (this.system == undefined) {
            this.system = '-1';
        }

        this.context?.templateUpdated.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
                this.updateLinkedItems();
                this.calculateFieldValueClass();
            }
        );
        this.context?.selectedElementsChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.calculateFieldValueClass());

        this.updateLinkedItems();
        this.calculateFieldValueClass();
    }

    public update(event = null, mimeChange = null) {
        if ((mimeChange != null && mimeChange) || (event != null && event.previousValue !== event.value)) {
            this.model.mimes.forEach((mime: Mime) => {
                mime.lastChanged = new Date();
            });
        }

        this.onValueChanged.emit(event);
    }

    public elementSelectFunction() {
        this.elementSelected.emit(this.field);
    }

    public switchValueChanged(event) {
        this.showHTMLEditorChange.emit(event.value);
    }

    get hasTranslation(): Boolean {
        return this.productTranslation != null;
    }

    get contentWidth(): string {
        return this.suppressContentWidth ? '' : 'w-100';
    }

    public calculateFieldValueClass() {
        let compare = this.field;
        if (this.system != '0' && this.system != '-1') {
            compare = compare + '_' + this.system;
            if (this.element != '0' && this.element != '-1') {
                compare = compare + '_' + this.element;
            }
        }

        let classes = '';
        if (this.context?.selectedElements.some(x => x == compare)) {
            classes += ' selectedField';
        }

        this.showRemoveFromTemplateItem = false;
        if (this.context?.templateView && this.linkedItems.length > 0) {
            classes += ' usedField';
            this.showRemoveFromTemplateItem = true;
        }

        this.fieldValueClass = classes;
    }

    async removeFromTemplateItem(event) {
        let pimField = new NextPimField(this.element, this.system, this.field);
        this.context.pimFieldCleared.emit(pimField);
    }

    isVirtualCatalog(): boolean {
        return this.catalogService?.catalog?.isVirtual;
    }

    toggleToolTip(visible: boolean) {
        if (this.showToolTips) {
            this.toolTipVisible = visible;
        }
    }

    toolTipContent() {
        return this.toolTipServiceService.getToolTipContent(this.field, this.showProToolTips);
    }

    onDragStart(e) {
        // [data]="{ dModel: model, dField: field, dSystem: system, dElement: element }"
        if (!this.showDragHandle) {
            e.cancel = true;
            return;
        }

        if (e.fromData.dModel === undefined || e.fromData.dModel === null) {
            e.cancel = true;
            return;
        }

        if (e.fromData.dField === 'MIME_ORDER') {
            e.cancel = true;
            return;
        }

        e.itemData = e.fromData;
        e.itemData.source = 'product';

        this.templateService
            .getHeadline(this.field, this.element, this.system, this.loginService.currentCustomer.id)
            .subscribe((result: StringResponse) => {
                this.dragInfo.dDisplayName = result.value;
            });

    }

    onDropAdd(e: {
        itemData?: ExcelElement & { exportMode?: boolean };
        newField?: string;
        newSystem?: string;
        newElement?: string;
    }) {
        if (e.itemData.exportMode)
            return;

        e.newField = this.field;
        e.newSystem = this.system;
        e.newElement = this.element;
        this.excelElementDropped.emit(e);
    }


    public hasMasterOrChildOrIsTranslated(): boolean {
        return this.model == null || this.hasTranslation ? false : this.model.hasMasterOrChild();
    }

    public isFieldLinked(field: string, system?: string, element?: string): boolean {
        return this.model.isFieldLinked(field, system, element);
    }

    public async toggleLinkedField(field: string, system?: string, element?: string, event = null) {
        if (!this.userManagement.canEdit('inputControlLink')) {
            return;
        }

        if (this.isFieldLinked(field, system, element)) {
            if (field == 'REFERENCE_FEATURE_SYSTEM_NAME') {
                let result = await confirm(
                    this.translate.instant('RemoveLinkedFieldFeatureSystemNameConfirmMessage'),
                    this.translate.instant('RemoveLinkedFieldConfirmTitle')
                );
                if (!result) {
                    return;
                }
            }
            if (field == 'FNAME' || field == 'FUNIT') {
                let result = await confirm(
                    this.translate.instant('RemoveLinkedFieldFeatureNameUnitConfirmMessage'),
                    this.translate.instant('RemoveLinkedFieldConfirmTitle')
                );
                if (!result) {
                    return;
                }
            }
        }

        let changed = this.model.toggleLinkedField(field, system, element);
        if (!changed) {
            return;
        }
        event.toggleLinkedFields = true;

        let isNowLinked = this.isFieldLinked(field, system, element);
        event.target.className = this.model.getClassForLinkedField(isNowLinked);

        if (this.model.isChild && isNowLinked) {
            event.forceReload = true;
        }

        if (field == 'DESCRIPTION_SHORT' || field == 'SUPPLIER_PID' || field == 'MIME_PURPOSE' || field == 'MIME_SOURCE') {
            event.forceTreeRefresh = true;
        }

        if (
            field == 'MIME_TYPE' ||
            field == 'MIME_SOURCE' ||
            field == 'MIME_DESCR' ||
            field == 'MIME_ALT' ||
            field == 'MIME_PURPOSE'
        ) {
            this.update(event, true);
        } else {
            this.update(event);
        }
    }

    public getInitialClassForLinkedField(field: string, system?: string, element?: string): string {
        let isNowLinked = this.isFieldLinked(field, system, element);
        if (!isNowLinked && !this.userManagement.canView('inputControlLink')) {
            return 'link-placeholder';
        }
        return this.model.getClassForLinkedField(isNowLinked);
    }

    canView(name: string): boolean {
        return this.userManagement.canView(name);
    }

    readOnlyUser(name: string): boolean {
        return !this.userManagement.canEdit(name);
    }

    get hasMultiplePimFields() {
        return this.linkedItems.length > 1;
    }

    updateLinkedItems() {
        if (!this.context?.excelModel) {
            return;
        }
        this.linkedItems = this.context.excelModel.filter(
            (i) =>
                i.pimFields != undefined &&
                i.pimFields.findIndex((x) => x.field == this.field && x.systemKey == this.system && x.elementKey == this.element) != -1
        );

        this.linkedItems = this.linkedItems.sort((a, b) => {
                let relevantPimFieldA = a.pimFields.find((x) => x.field == this.field && x.systemKey == this.system && x.elementKey == this.element);
                let relevantPimFieldB = b.pimFields.find((x) => x.field == this.field && x.systemKey == this.system && x.elementKey == this.element);
                return relevantPimFieldA?.order - relevantPimFieldB.order;
            }
        );
    }

    deleteLinkedItem(e: ItemDeletingEvent) {
        e.itemData.pimFields = e.itemData.pimFields.filter((x) => !(x.field == this.field && x.systemKey == this.system && x.elementKey == this.element));
        this.context?.pimFieldReordered.emit(this.field);
    }

    updateLinkedItemOrder(e) {
        var movedFields = this.linkedItems.splice(e.fromIndex, 1);
        this.linkedItems.splice(e.toIndex, 0, ...movedFields);

        this.linkedItems.forEach((item, index) => {
            let relevantPimField = item.pimFields.find((x) => x.field == this.field && x.systemKey == this.system && x.elementKey == this.element);
            if (!item)
                return;

            relevantPimField.order = index;
        });

        this.context?.pimFieldReordered.emit(this.field);
    }
}
